import React, { useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import { DIALOG_HEIGHT } from "./dialogSize";
import { VisibilityIcon, valueOf } from "./SettingsView";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* The values for the {VARIABLES} the Artifacts use ({BASE_URL}, {ADMIN_USER},
   {ADMIN_PASSWORD}, …). An Artifact keeps its placeholders, so the same one
   runs as any user.

   mode "execute" (Execute, first step): every value is needed, for this run
   only. It starts with the values kept with the Artifact at Create, and any
   can be changed. A secret (PASSWORD / SECRET / PIN) is a password field,
   never kept and typed each time. A user + password pair signs the run in as
   that user, and {BASE_URL} is where it opens.

   mode "create" (Create Artifact, before the queue): the values Save keeps
   with the Artifacts, which Execute starts with. Each is optional, and
   secrets are not asked at all.

   With `userkey`, the › button next to each value opens ConfigPicker: every
   entry of Settings › Configuration (your Private ones and every Public one),
   whatever its name, since a name there can differ from the test's (DEV_URL
   for {BASE_URL}). One entry fills one field, its value going in as if
   typed. */

/* A secret: PASSWORD, SECRET or PIN as a whole part of its name (as the API). */
export const isSecretName = (name) => /(?:^|_)(?:PASSWORD|SECRET|PIN)(?:_|$)/.test(name);

/* [{name, tests: [key…], secret, meaning, value, reference}] — each variable
   once, in order of first use across the tests, from their Artifacts' own
   list (`variables: [{name, secret, meaning, value?, reference?}]`). `value`
   is the first one kept with them; `reference` the requirement file's value
   for its reference environment. A secret never has either. */
export function runVariablesOf(tests) {
  const found = new Map();
  for (const t of tests || []) {
    for (const v of t.variables || []) {
      if (!v?.name) continue;
      if (!found.has(v.name)) {
        const secret = Boolean(v.secret) || isSecretName(v.name);
        found.set(v.name, { name: v.name, tests: [], secret, meaning: v.meaning || "", value: "", reference: "" });
      }
      const entry = found.get(v.name);
      if (!entry.tests.includes(t.key)) entry.tests.push(t.key);
      if (!entry.meaning && v.meaning) entry.meaning = v.meaning;
      if (!entry.value && !entry.secret && v.value) entry.value = String(v.value);
      if (!entry.reference && !entry.secret && v.reference) entry.reference = String(v.reference);
    }
  }
  return [...found.values()];
}

/* The picker's entries (GET variables: your Private ones and every Public
   one), whatever their name: the ones named like the field first, your
   Private one before the Public one, then by name. */
export function pickerRows(rows, name) {
  const rank = (r) => (r.name !== name ? 2 : r.visibility === "private" ? 0 : 1);
  return [...(rows || [])].sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
}

/* Why a configuration entry can't fill a field ("" when it can). */
export function unusable(row) {
  return valueOf(row) ? "" : "No value set";
}

/* "Private", "Public", "Public, by Osama" (no "yours"). */
const kindOf = (row) =>
  row.visibility === "private" ? "Private" : row.is_owner ? "Public" : `Public, by ${row.owner?.name || "another user"}`;
export const sourceLabel = (row) => `${row.name} (${kindOf(row)})`;

/* "Choose a value for {NAME}": every configuration entry on one line (its
   Private / Public icon, name, value, and the → button that fills the field
   and closes this), under the column headers Configuration, Value and
   Action. Cancel, in the footer as the confirmation's, has the focus first;
   Escape (handled by the dialog under it) closes this one only. */
function ConfigPicker({ name, rows, state, current, onUse, onClose }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  const list = pickerRows(rows, name);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default bg-overlay/40" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Choose a value for ${name}`}
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200`}
      >
        {/* Header — the primary theme colour, as on the dialog under it. */}
        <div className="flex flex-none items-center gap-2.5 bg-brand-700 px-5 py-3.5 text-white">
          <Icons.Configuration className="h-5 w-5 flex-none" />
          <h3 title={`Choose a value for {${name}}`} className="min-w-0 flex-1 truncate text-base font-semibold">
            Choose a value for <span className="font-mono">{`{${name}}`}</span>
          </h3>
        </div>
        {/* The columns' headers, over the list (they stay while it scrolls):
            Configuration spans the icon and the name (w-5 + gap-3 + w-40). */}
        {state === "ready" && list.length > 0 && (
          <div
            data-testid="picker-columns"
            className="flex flex-none items-center gap-3 border-b border-slate-200 bg-slate-50 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500"
          >
            <span className="w-48 flex-none">Configuration</span>
            <span className="min-w-0 flex-1">Value</span>
            <span className="w-14 flex-none text-center">Action</span>
          </div>
        )}
        <div className="cq-scroll min-h-0 flex-1 overflow-y-auto">
          {state === "loading" ? (
            <p className="px-5 py-8 text-center text-sm text-slate-400">Reading your configuration…</p>
          ) : state === "error" ? (
            <p className="px-5 py-8 text-center text-sm text-rose-600">Your configuration could not be read. Type the value instead.</p>
          ) : list.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-500">
              Your configuration has no entries yet. Create them in Settings › Configuration.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.map((r) => {
                const why = unusable(r);
                const inUse = current === r.variablekey;
                return (
                  <li
                    key={r.variablekey}
                    title={`${r.name} — ${kindOf(r)}`}
                    className={`flex items-center gap-3 px-5 py-2.5 ${inUse ? "bg-brand-50/60" : ""}`}
                  >
                    <VisibilityIcon visibility={r.visibility} />
                    <span className="w-40 flex-none truncate font-mono text-[13px] font-semibold text-slate-800">{r.name}</span>
                    <span className={`min-w-0 flex-1 truncate text-[13px] ${why ? "text-slate-400" : "font-mono text-slate-600"}`}>
                      {why || valueOf(r)}
                    </span>
                    {/* The Action column. */}
                    <span className="flex w-14 flex-none justify-center">
                      <button
                        type="button"
                        onClick={() => onUse(r)}
                        disabled={Boolean(why)}
                        aria-label={`Use ${r.name} for ${name}`}
                        title={why || (inUse ? `In use for ${name}` : `Use ${r.name} for ${name}`)}
                        className={[
                          "flex h-8 w-8 flex-none items-center justify-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-40",
                          inUse
                            ? "border-brand-600 bg-brand-600 text-white"
                            : "border-slate-200 bg-card text-brand-600 hover:border-brand-300 hover:bg-brand-50",
                        ].join(" ")}
                      >
                        {inUse ? <Icons.Check className="h-4 w-4" /> : <Icons.ArrowRight className="h-4 w-4" />}
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {/* Footer: Cancel, outlined in the theme colour, as the confirmation's. */}
        <div className="flex flex-none justify-end border-t border-slate-200 bg-brand-50/60 px-5 py-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            className="min-w-[8rem] rounded-md border-2 border-brand-700 bg-card px-6 py-2 text-base font-semibold text-brand-700 transition hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const INTRO = {
  execute:
    "Enter the value of each variable the Artifact uses, for this run only. Values kept with the Artifact when it " +
    "was created, or else the requirement file's reference values, are filled in; change any of them for this run. A user and password sign the run in as that user, " +
    "and {BASE_URL} is the address it opens. Passwords are never stored; the result shows them as ••••. " +
    "The › button next to a value takes one from your Settings › Configuration, under any name. " +
    "A value the requirement file says where to read " +
    "(its Live values) is filled in from the PlantOps app at {BASE_URL}, as it holds it now.",
  create:
    "Enter the values these tests use; the requirement file's reference values are filled in. Save keeps them " +
    "with the Artifacts, and Execute starts with them, where you " +
    "can change any. Leave one empty to type it at Execute. Passwords aren't asked here: they are typed at each " +
    "Execute and never stored. A value taken from your configuration is kept like a typed one, as the default " +
    "for everyone who runs these Artifacts. A value the requirement file says where to read (its Live values) is " +
    "filled in from the PlantOps app at {BASE_URL}, as it holds it now.",
};

/* The dialog's sections: variables more than one test uses ("shared"), then
   each test with the variables only it uses, in the tests' order. */
export function groupByTest(variables, tests) {
  const shared = variables.filter((v) => v.tests.length > 1);
  const order = [...(tests || []).map((t) => t.key), ...variables.flatMap((v) => v.tests)];
  const keys = [...new Set(order)];
  const own = keys
    .map((key) => ({
      key,
      summary: (tests || []).find((t) => t.key === key)?.summary || "",
      variables: variables.filter((v) => v.tests.length === 1 && v.tests[0] === key),
    }))
    .filter((g) => g.variables.length);
  return { shared, own };
}

/* `extraFields` ([{name, label, hint, optional}]) adds plain inputs, required
   unless `optional`, above the variables (For you › Test Runs: the
   execution's Build Number / Planned, Affects and Fix versions when Jira has
   none yet); their values come back as onExecute's 2nd arg. */
export default function RunVariablesDialog({
  title = "Values for this run",
  mode = "execute",
  tests,
  extraFields = [],
  userkey = null,
  onExecute,
  onCancel,
}) {
  const creating = mode === "create";
  const variables = useMemo(
    () => runVariablesOf(tests).filter((v) => !(creating && v.secret)),
    [tests, creating],
  );
  /* Start with the values kept with the Artifacts, else the requirement
     file's reference values (none for a secret). */
  const [values, setValues] = useState(() =>
    Object.fromEntries(
      runVariablesOf(tests)
        .filter((v) => v.value || v.reference)
        .map((v) => [v.name, v.value || v.reference]),
    ),
  );
  const [extra, setExtra] = useState({});
  const [shown, setShown] = useState({});
  /* Settings › Configuration: the rows (GET variables) and whether they
     loaded, the entry each field took its value from, and the field whose
     picker is open. */
  const [config, setConfig] = useState([]);
  const [configState, setConfigState] = useState("loading");
  const [picked, setPicked] = useState({});
  const [pickerFor, setPickerFor] = useState("");
  /* What the PlantOps app at {BASE_URL} holds now for these names
     (GET plantops/current-values: the names the tests' requirement file
     lists under "Live values"), its
     host, and the fields typed in since the dialog opened, which it never
     overwrites. */
  const [live, setLive] = useState({ values: {}, source: "" });
  const touched = useRef(new Set());

  /* Escape closes the picker when it is open, else this dialog. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (pickerFor) setPickerFor("");
      else onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, pickerFor]);

  useEffect(() => {
    if (!userkey) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}variables?userkey=${encodeURIComponent(userkey)}`);
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok && Array.isArray(body?.data)) {
          setConfig(body.data);
          setConfigState("ready");
        } else setConfigState("error");
      } catch {
        if (alive) setConfigState("error"); // every field can still be typed
      }
    })();
    return () => {
      alive = false;
    };
  }, [userkey]);

  /* Fill the values the app holds now (they can still be changed): typing
     the policy's baseline by hand put 90 and 6 into the wrong boxes of
     PSR-2118 and PSR-2120. Read again when {BASE_URL} changes. */
  const baseUrl = String(values.BASE_URL || "").trim();
  const liveNames = useMemo(
    () => variables.filter((v) => !v.secret && v.name !== "BASE_URL").map((v) => v.name).join(","),
    [variables],
  );
  const liveKeys = useMemo(() => (tests || []).map((t) => t.key).filter(Boolean).join(","), [tests]);
  useEffect(() => {
    if (!baseUrl || !liveNames || !liveKeys) return undefined;
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}plantops/current-values?base_url=${encodeURIComponent(baseUrl)}&names=${encodeURIComponent(liveNames)}&keys=${encodeURIComponent(liveKeys)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!alive || !res.ok || !body?.data?.values) return;
        const got = body.data.values;
        setLive({ values: got, source: body.data.source || "" });
        setValues((prev) => {
          const next = { ...prev };
          for (const [name, value] of Object.entries(got)) if (!touched.current.has(name)) next[name] = value;
          return next;
        });
      } catch {
        /* PlantOps can't be read: the values stay as they are. */
      }
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [baseUrl, liveNames, liveKeys]);

  const trimmed = useMemo(
    () => Object.fromEntries(variables.map((v) => [v.name, String(values[v.name] || "").trim()])),
    [variables, values],
  );
  const extraTrimmed = Object.fromEntries(extraFields.map((f) => [f.name, String(extra[f.name] || "").trim()]));
  const isFilled = (name) => Boolean(trimmed[name]);
  /* At Create every value is optional: one left empty is typed at Execute. */
  const complete =
    creating || (variables.every((v) => isFilled(v.name)) && extraFields.every((f) => f.optional || extraTrimmed[f.name]));

  const submit = (e) => {
    e.preventDefault();
    if (complete) onExecute(trimmed, extraTrimmed);
  };

  /* Take a field's value from a configuration entry, as if typed. */
  const pick = (name, row) => {
    touched.current.add(name);
    setPickerFor("");
    setValues((prev) => ({ ...prev, [name]: valueOf(row) }));
    setPicked((prev) => ({ ...prev, [name]: row }));
  };
  const unpick = (name) =>
    setPicked((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/25";

  const { shared, own } = useMemo(() => groupByTest(variables, tests), [variables, tests]);

  const field = (v) => {
    const id = `var-${v.name}`;
    const from = picked[v.name];
    return (
      <div key={v.name}>
        <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <Icons.Variable className="h-4 w-4 flex-none text-brand-600" />
          <span className="font-mono">{`{${v.name}}`}</span>
          {!creating && <span className="text-rose-500">*</span>}
        </label>
        {v.meaning && <p className="mt-0.5 text-[12px] text-slate-500">{v.meaning}</p>}
        {v.reference && String(values[v.name] || "").trim() !== v.reference && (
          <p className="mt-0.5 text-[12px] text-amber-700">
            The requirement file's reference value is <span className="font-mono">{v.reference}</span>.{" "}
            <button
              type="button"
              onClick={() => {
                touched.current.add(v.name);
                setValues((prev) => ({ ...prev, [v.name]: v.reference }));
                unpick(v.name);
              }}
              className="font-semibold text-brand-700 underline"
            >
              Use it
            </button>
          </p>
        )}
        {live.values[v.name] !== undefined &&
          (String(values[v.name] || "").trim() === live.values[v.name] ? (
            <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-brand-700">
              <Icons.Check className="h-3.5 w-3.5 flex-none" />
              <span>Filled in from PlantOps ({live.source}): what the app holds now.</span>
            </p>
          ) : (
            <p className="mt-0.5 text-[12px] text-amber-700">
              PlantOps ({live.source}) holds <span className="font-mono">{live.values[v.name]}</span> now.{" "}
              <button
                type="button"
                onClick={() => {
                  setValues((prev) => ({ ...prev, [v.name]: live.values[v.name] }));
                  unpick(v.name);
                }}
                className="font-semibold text-brand-700 underline"
              >
                Use it
              </button>
            </p>
          ))}
        <div className="mt-1.5 flex items-stretch gap-2">
          <div className="relative min-w-0 flex-1">
            <input
              id={id}
              type={v.secret && !shown[v.name] ? "password" : "text"}
              value={values[v.name] || ""}
              onChange={(e) => {
                touched.current.add(v.name);
                setValues((prev) => ({ ...prev, [v.name]: e.target.value }));
                unpick(v.name); // typed: no longer the configuration's value
              }}
              /* Chrome ignores "off" next to a password field and fills in a
                 saved sign-in; "new-password" stops it, and a unique name keeps
                 it from matching anything it has saved. */
              name={`cortex-var-${v.name}`}
              autoComplete={v.secret ? "new-password" : "off"}
              className={`${inputCls} ${v.secret ? "pr-10" : ""}`}
            />
            {v.secret && (
              <button
                type="button"
                onClick={() => setShown((prev) => ({ ...prev, [v.name]: !prev[v.name] }))}
                aria-label={shown[v.name] ? `Hide ${v.name}` : `Show ${v.name}`}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-700"
              >
                {shown[v.name] ? <Icons.EyeOff className="h-4 w-4" /> : <Icons.Eye className="h-4 w-4" />}
              </button>
            )}
          </div>
          {/* › Choose a value from the configuration, under any name. */}
          {userkey && (
            <button
              type="button"
              onClick={() => setPickerFor(v.name)}
              aria-label={`Choose a value for ${v.name} from your configuration`}
              aria-haspopup="dialog"
              title="Choose a value from your configuration"
              className="flex w-10 flex-none items-center justify-center rounded-lg border border-slate-300 bg-card text-brand-600 transition hover:bg-brand-50 hover:text-brand-700"
            >
              <Icons.ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {from && (
          <p className="mt-1 flex items-center gap-1.5 text-[12px] text-brand-700">
            <Icons.Configuration className="h-3.5 w-3.5 flex-none" />
            <span>From configuration: {sourceLabel(from)}</span>
          </p>
        )}
      </div>
    );
  };

  /* One card per group; its header says whose variables these are and how
     many are filled in. */
  const section = ({ id, label, header, vars }) => {
    const filled = vars.filter((v) => isFilled(v.name)).length;
    const done = filled === vars.length;
    return (
      <section key={id} aria-label={label} className="overflow-hidden rounded-xl border border-slate-200">
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          {header}
          <span
            className={`ml-auto flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              done ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
            }`}
          >
            {filled} of {vars.length} filled
          </span>
        </div>
        <div className="space-y-4 px-4 py-4">{vars.map((v) => field(v))}</div>
      </section>
    );
  };

  const testLabel = (key, summary) => (
    <span className="flex min-w-0 items-center gap-2">
      <Icons.JiraXrayTest />
      <span className="flex-none text-sm font-semibold text-brand-700">{key}</span>
      {summary && <span className="truncate text-sm text-slate-700">{summary}</span>}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onCancel} />
      <form
        onSubmit={submit}
        autoComplete="off"
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-vars-title"
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}
      >
        <div className="flex flex-none items-center gap-2.5 bg-brand-700 px-6 py-4 text-white">
          <Icons.Variable className="h-5 w-5" />
          <h2 id="run-vars-title" className="text-base font-semibold">
            {title}
          </h2>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <p className="text-[13px] text-slate-600">{INTRO[creating ? "create" : "execute"]}</p>
          {extraFields.length > 0 && (
            <section aria-label="Execution" className="space-y-4 rounded-xl border border-slate-200 px-4 py-4">
              {extraFields.map((f) => (
                <div key={f.name}>
                  <label htmlFor={`extra-${f.name}`} className="text-sm font-semibold text-slate-800">
                    {f.label}{" "}
                    {f.optional ? (
                      <span className="text-[12px] font-normal text-slate-500">(optional)</span>
                    ) : (
                      <span className="text-rose-500">*</span>
                    )}
                  </label>
                  {f.hint && <p className="mt-0.5 text-[12px] text-slate-500">{f.hint}</p>}
                  <input
                    id={`extra-${f.name}`}
                    value={extra[f.name] || ""}
                    onChange={(e) => setExtra((prev) => ({ ...prev, [f.name]: e.target.value }))}
                    autoComplete="off"
                    className={`${inputCls} mt-1.5`}
                  />
                </div>
              ))}
            </section>
          )}
          {shared.length > 0 &&
            section({
              id: "shared",
              label: "Shared by several tests",
              vars: shared,
              header: (
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-slate-800">Shared by</span>
                  {[...new Set(shared.flatMap((v) => v.tests))].map((key) => (
                    <span key={key} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700">
                      <Icons.JiraXrayTest />
                      {key}
                    </span>
                  ))}
                </span>
              ),
            })}
          {own.map((g) => section({ id: g.key, label: g.key, vars: g.variables, header: testLabel(g.key, g.summary) }))}
        </div>
        <div className="flex flex-none justify-end gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!complete}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
          >
            {creating ? <Icons.Spark className="h-4 w-4" /> : <Icons.Play className="h-4 w-4" />}
            {creating ? "Create" : "Execute"}
          </button>
        </div>
      </form>
      {pickerFor && (
        <ConfigPicker
          name={pickerFor}
          rows={config}
          state={configState}
          current={picked[pickerFor]?.variablekey}
          onUse={(row) => pick(pickerFor, row)}
          onClose={() => setPickerFor("")}
        />
      )}
    </div>
  );
}
